import React, { useState } from "react";

import { useTranslation } from "utils";

import { PracticeFCPO, RequirementLevel } from "models";

import { AlertBanner, Flex, Form, KlimRadioGroup, RadioChangeEvent, Tag, Trans } from "components";

import { isRequired, isRequiredForSaving } from "pages/app/all-field-view/utils/getInputRequirementConfig";

type PayoutOption = {
  label: string;
  value: boolean;
};

type TillageYesNoFormProps = {
  onChange: (e: RadioChangeEvent) => void;
  value: boolean | null;
  baselineYears: number[] | undefined;
  tillageApplications: PracticeFCPO[];
  disabled: boolean;
  year: number;
  requirementLevel?: RequirementLevel;
  hasMixedValues?: boolean;
  showMessage?: boolean;
};

export const TillageYesNoForm = ({
  onChange,
  value,
  baselineYears,
  tillageApplications,
  disabled,
  year,
  requirementLevel,
  hasMixedValues,
  showMessage,
}: TillageYesNoFormProps) => {
  const { t } = useTranslation(["allFieldView"]);
  const [isApplied, setIsApplied] = useState<boolean | null>(value);

  // Compare the baseline years from the carbon runner to the first tillage application year
  // in order to show (or not) the baseline tag
  const tillageApplicationWithYear = tillageApplications.find((tillage) => tillage.year !== undefined);

  const isBaseline = tillageApplicationWithYear?.year ? baselineYears?.includes(tillageApplicationWithYear.year) : baselineYears?.includes(year);

  const payoutOptions: PayoutOption[] = [
    {
      label: "cropForm.yes",
      value: true,
    },
    {
      label: "cropForm.no",
      value: false,
    },
  ];

  return (
    <Flex vertical className={`${!value ? "tillage-yes-no-form reduced-view" : "tillage-yes-no-form"}`} gap="0.75rem">
      <Flex gap="0.75rem">
        <h5>{t("tillageForm.title")}</h5>
        {isBaseline && (
          <Tag dataTestId="timeline-drawer-tillage-form-baseline-tag" className="info-tag-drawer" colour="grey" text={t("tillageForm.baseline")} />
        )}
      </Flex>
      <div className="form-container" data-testid="add-edit-tillage-yes-no-form">
        <Flex justify="space-between" gap="1rem" className="margin-top-100  margin-bottom-100">
          <p>
            {t("tillageForm.apply")} {isRequired(requirementLevel) && "*"}
          </p>
          <Form.Item
            name={"isTillageApplied"}
            initialValue={isApplied}
            rules={[{ required: isRequiredForSaving(requirementLevel), message: t("tillageForm.errorMessages.tillageRequired") }]}
            help={hasMixedValues ? t("tillageForm.helperTextYesNoQuestion") : undefined}
          >
            <KlimRadioGroup
              className="residue-button"
              size="large"
              type="tertiary"
              dataTestId="tillage-residue-result-toggle"
              options={payoutOptions.map((option) => ({
                label: (
                  <Flex vertical justify="space-around">
                    <span>{t(option.label)}</span>
                  </Flex>
                ),
                value: option.value,
              }))}
              value={isApplied}
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => {
                onChange(e);
                setIsApplied(e.target.value);
              }}
              fullWidth
              disabled={disabled}
            />
          </Form.Item>
        </Flex>
        {showMessage && (
          <AlertBanner
            className="margin-top-200 margin-bottom-200"
            description={<Trans i18nKey={"allFieldView:cropForm.multiplePracticesMessage"} components={{ strong: <strong /> }} />}
            type="make-aware"
            iconName="tabler/info-circle"
            iconAlign="start"
          />
        )}
      </div>
    </Flex>
  );
};
